

const EMPTY_TEXT = '';
const DEFAULT_CURRENCY = 'BYN';
const DECIMAL_COMMA = ',';
const DECIMAL_POINT = '.';
const ZERO_DIGIT = '0';
const CENTS_PADDING = '00';
const DECIMAL_BASE = 10;
const HALF_YEAR_FACTOR = 2;
const DECIMAL_PLACES = 2;
const ZERO_VALUE = 0;
const FIRST_DECIMAL_PART = 1;
export interface SuggestionEconomicEstimate {
  money: string | null;
  hours: string | null;
  comment: string | null;
}

export interface SuggestionEconomics {
  currency: string;
  annualSavings?: SuggestionEconomicEstimate;
  implementation?: SuggestionEconomicEstimate;
  correction?: SuggestionEconomicEstimate;
}

export const ECONOMIC_SECTIONS = [
  {key: 'annualSavings', label: 'SUGGESTION_ANNUAL_SAVINGS'},
  {key: 'implementation', label: 'SUGGESTION_IMPLEMENTATION_COST'},
  {key: 'correction', label: 'SUGGESTION_CORRECTION_COST'}
];

export function emptySuggestionEconomics(isError: boolean = false): SuggestionEconomics {
  const estimate = (): SuggestionEconomicEstimate => ({money: EMPTY_TEXT, hours: EMPTY_TEXT, comment: EMPTY_TEXT});
  return isError ? {currency: DEFAULT_CURRENCY, correction: estimate()}
    : {currency: DEFAULT_CURRENCY, annualSavings: estimate(), implementation: estimate()};
}

export function normalizeEconomicDecimal(value: string): string | null {
  return value == null || value.trim() === EMPTY_TEXT ? null : value.trim().replace(DECIMAL_COMMA, DECIMAL_POINT);
}

export function validEconomicDecimal(value: string): boolean {
  const normalized = normalizeEconomicDecimal(value);
  return normalized === null || /^[0-9]{1,18}(\.[0-9]{1,2})?$/.test(normalized);
}

export function normalizeSuggestionEconomics(value: SuggestionEconomics): SuggestionEconomics {
  const result: SuggestionEconomics = {currency: value.currency};
  ECONOMIC_SECTIONS.forEach(section => {
    const estimate: SuggestionEconomicEstimate = value[section.key];
    if (estimate) {
      result[section.key] = {money: normalizeEconomicDecimal(estimate.money),
        hours: normalizeEconomicDecimal(estimate.hours), comment: estimate.comment ? estimate.comment.trim() : null};
    }
  });
  return result;
}

export function restoreSuggestionEconomics(value: SuggestionEconomics): SuggestionEconomics {
  const result = emptySuggestionEconomics();
  if (!value) { return result; }
  result.currency = value.currency || DEFAULT_CURRENCY;
  if (value.annualSavings) { result.annualSavings = Object.assign({}, value.annualSavings); }
  if (value.implementation) { result.implementation = Object.assign({}, value.implementation); }
  return result;
}

// null means there is no valid numeric estimate, including correction-only economics.
export function suggestionPaysBackInHalfYear(value: SuggestionEconomics): boolean | null {
  if (!value || value.correction || (!value.annualSavings && !value.implementation)) { return null; }
  const savings = value.annualSavings || {money: null, hours: null, comment: null};
  const costs = value.implementation || {money: null, hours: null, comment: null};
  const values = [savings.money, savings.hours, costs.money, costs.hours];
  if (!values.every(validEconomicDecimal) || !values.some(item => normalizeEconomicDecimal(item) !== null)) {
    return null;
  }
  return coversHalfYear(savings.money, costs.money) && coversHalfYear(savings.hours, costs.hours);
}

function coversHalfYear(savings: string, costs: string): boolean {
  const available = economicCents(savings);
  const required = doubleDigits(economicCents(costs));
  return available.length !== required.length ? available.length > required.length : available >= required;
}

function economicCents(value: string): string {
  const parts = (normalizeEconomicDecimal(value) || ZERO_DIGIT).split(DECIMAL_POINT);
  return (parts[ZERO_VALUE] + ((parts[FIRST_DECIMAL_PART] || EMPTY_TEXT) + CENTS_PADDING)
    .slice(ZERO_VALUE, DECIMAL_PLACES)).replace(/^0+(?=\d)/, EMPTY_TEXT);
}

function doubleDigits(value: string): string {
  let result = EMPTY_TEXT;
  let carry = ZERO_VALUE;
  for (let index = value.length - FIRST_DECIMAL_PART; index >= ZERO_VALUE; index--) {
    const digit = Number(value.charAt(index)) * HALF_YEAR_FACTOR + carry;
    result = String(digit % DECIMAL_BASE) + result;
    carry = Math.floor(digit / DECIMAL_BASE);
  }
  return carry ? String(carry) + result : result;
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/suggestion-lists/models/suggestion-economics.model.ts